import React, { useContext, useRef, useState, useEffect, useCallback } from "react";
import studyReportContext from "../../../../../context/studyreport/StudyReportContext";
import MicIcon from "../../../../../components/SVGComponents/MicIcon";
import CustomToolTipHost from "../../../../../components/CustomToolTipHost/CustomToolTipHost";
import { useTranslation } from "react-i18next";
import FroalaEditor from "froala-editor";

const MicToolComponent = () => {
  const {
    editorRef,
    activeField,
    setObservationState,
    setFindingSentence,
    setInputValue,
    chatInputRef  
  } = useContext(studyReportContext);

  const [t] = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const webSocketRef = useRef(null);
  const keepAliveRef = useRef(null);
  const mediaStreamRef = useRef(null); // Store the audio stream
  const silenceTimerRef = useRef(null); 
  const insertTextRef = useRef(null);
  const info = (message) => console.log(message); // Logging function
  const errorLog = (message, error) => console.error(message, error);

  const insertText = useCallback((transcript) => {
    const text = transcript + " ";
    if (activeField === 'chatInput' && chatInputRef?.current) {
      const ta = chatInputRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const before = ta.value.slice(0, start);
      const after = ta.value.slice(end);
      const newValue = before + text + after;
      setInputValue(newValue);
      setTimeout(() => {
        ta.focus();
        const pos = start + text.length;
        ta.selectionStart = ta.selectionEnd = pos;
        // Auto-scroll so cursor is in view
        const lineHeight = parseInt(window.getComputedStyle(ta).lineHeight);
        const cursorLine = Math.floor(pos / ta.cols);
        ta.scrollTop = cursorLine * lineHeight;
      }, 0);
      return;
    }

    //handle updates 
    setObservationState(prev => {
      const newState = { ...prev };
      switch(activeField) {
        case 'clinicalHistory':
          newState.clinicalHistory = (prev.clinicalHistory || '') + text;
          break;
        case 'comparison':
          newState.comparison = (prev.comparison || '') + text;
          break;
        case 'technique':
          newState.technique = (prev.technique || '') + text;
          break;
        default:
          break;
      }
      return newState;
    });

    // Handle findings here
    if (activeField === 'findings') {
      setFindingSentence(prev => (prev || '') + text);
    }

    // handle editor here
    if (!activeField && editorRef?.current?.editor) {
      editorRef.current.editor.html.insert(text);
    }
  }, [activeField,chatInputRef, setInputValue, setObservationState, setFindingSentence, editorRef]);

  // Keep ref updated
  useEffect(() => {
    insertTextRef.current = insertText;
  }, [insertText]);

  const stopRecording = () => {
    if (webSocketRef.current) {
      webSocketRef.current.close();
      webSocketRef.current = null;
    }

    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }

    // Stop all tracks on the audio stream to release the microphone
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    setIsRecording(false);
    info("Recording stopped.");
  };

  const startRecording = async () => {
    info("Microphone clicked to start recording. ");
    const DEEPGRAM_API_URL = process.env.REACT_APP_DEEPGRAM_API_URL;
    const DEEPGRAM_API_KEY = process.env.REACT_APP_DEEPGRAM_API_KEY;

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ 
        audio: true,
      });
      // Store the audio stream in a ref to manage it later
      mediaStreamRef.current = audioStream;
      const mediaRecorder = new MediaRecorder(audioStream);

       if (mediaRecorder) {
         // Create WebSocket connection to Deepgram API
         const newWebSocket = new WebSocket(DEEPGRAM_API_URL, [
           "token",
           DEEPGRAM_API_KEY,
         ]);
         webSocketRef.current = newWebSocket;
        // Handle WebSocket open event
        newWebSocket.onopen = () => {
          info("WebSocket connection established.");

          mediaRecorder.addEventListener("dataavailable", (event) => {
            if (event.data.size > 0 && newWebSocket.readyState === 1) {
              newWebSocket.send(event.data);
            }
          });

          mediaRecorder.start(20);

          keepAliveRef.current = setInterval(() => {
            const keepAliveMsg = JSON.stringify({ type: "KeepAlive" });
            newWebSocket.send(keepAliveMsg);
            info("Sent KeepAlive message.");
          }, 3000);
        };
      // Handle WebSocket message event
      newWebSocket.onmessage = message => {
        const data = JSON.parse(message.data);
        const transcript = data?.channel?.alternatives?.[0]?.transcript;
        if (transcript && data.is_final && insertTextRef.current) {
          insertTextRef.current(transcript);
          resetSilenceTimer();
         }
      };
      
      // Handle WebSocket close event
        newWebSocket.onclose = () => {
          info("WebSocket connection closed.");
        };

        // Handle WebSocket error event
        newWebSocket.onerror = (error) => {
          info("WebSocket error.", { error });
          stopRecording();
        };

        setIsRecording(true);

        // Start the silence timer initially
        resetSilenceTimer();
       }
    } catch (error) {
      errorLog("Microphone permission denied.", { error });
      setIsRecording(false);
    }
  };

  const resetSilenceTimer = () => {
    info("Silence timer reset.");
    // Clear any existing silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }

    // Start a new 10-second silence timer
    silenceTimerRef.current = setTimeout(() => {
      console.log("Silence detected for 60 seconds. Stopping recording...");
      stopRecording(); // Stop recording if no speech for 60 seconds
    }, 1000*60); // 60 seconds
  };

  const toggleRecording = () => {
    setIsRecording((prevState) => !prevState);
    isRecording ? stopRecording() : startRecording();
  };

  return (
    <>
      <CustomToolTipHost
        content={isRecording ? t("heading.mic-stop") : t("heading.mic-start")}
        id="micIcon"
        tooltipProps={{
          calloutProps: {
            styles: {
              beak: { background: "#252525;" },
              beakCurtain: { background: "#252525;" },
              calloutMain: { background: "#252525;" },
              borderRadius: "20%",
            }, 
          },
        }}
      >
        <span
          className="topbar-toolbar-lock-warning"
          onClick={toggleRecording}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <MicIcon isRecording={isRecording} />
        </span>
      </CustomToolTipHost>
    </>
  );
};

export default MicToolComponent;